/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useState, useEffect, useCallback } from "react";
import { ref, set, remove, onValue } from "firebase/database";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useSafeCircleStore } from "@/stores/safeCircleStore";
import { DEFAULT_PRIVACY_BY_MODE, AVATAR_COLORS } from "@/utils/safecircle/constants";
import { migrateLegacyMode } from "@/utils/safecircle/migrateMode";
import { captureException } from "@/utils/errorLogger";
import type { ProtectedProfile, SafeCircleMode, Relationship } from "@/types/safecircle";

export function useSafeCircleProfiles() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<ProtectedProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const setActiveProfile = useSafeCircleStore((s) => s.setActiveProfile);

  useEffect(() => {
    if (!user) {
      setProfiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const profilesRef = ref(db, `safecircle/${user.uid}/profiles`);
    const unsub = onValue(
      profilesRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data && typeof data === "object") {
          const list = Object.entries(data).map(
            ([id, val]: [string, any]) =>
              migrateLegacyMode({ ...val, profileId: id }) as ProtectedProfile,
          );
          setProfiles(list);
        } else {
          // First-time users have no profiles node — resolve to empty list,
          // never leave the UI stuck in skeleton state.
          setProfiles([]);
        }
        setLoading(false);
      },
      (err) => {
        captureException(err as Error, {
          where: "useSafeCircleProfiles.onValue",
          uid: user.uid,
        });
        setProfiles([]);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [user]);

  const addProfile = useCallback(
    async (name: string, mode: SafeCircleMode, relationship: Relationship) => {
      if (!user) return null;
      const profileId = `sc_${Date.now()}`;
      const now = Date.now();
      const profile: ProtectedProfile = {
        profileId,
        name: name.trim(),
        mode,
        relationship,
        avatarColor: AVATAR_COLORS[profiles.length % AVATAR_COLORS.length],
        privacyLevel: DEFAULT_PRIVACY_BY_MODE[mode],
        isActive: true,
        createdAt: now,
        updatedAt: now,
      };
      const path = `safecircle/${user.uid}/profiles/${profileId}`;
      try {
        await set(ref(db, path), profile);
      } catch (err) {
        const e = err as Error & { code?: string };
        if (import.meta.env.DEV) {
          console.error("[useSafeCircleProfiles.addProfile] Firebase write failed", {
            code: e.code,
            message: e.message,
            path,
            uid: user.uid,
            profileId,
          });
        }
        captureException(e, {
          where: "useSafeCircleProfiles.addProfile",
          code: e.code ?? "unknown",
          message: e.message,
          path,
          uid: user.uid,
          profileId,
        });
        return null;
      }
      // Activate the new profile if this is the user's first one. We check
      // the current store value to avoid a stale closure race where the
      // onValue listener hasn't fired yet.
      const activeId = useSafeCircleStore.getState().activeProfileId;
      if (profiles.length === 0 || !activeId) {
        setActiveProfile(profileId);
      }
      return profileId;
    },
    [user, profiles.length, setActiveProfile],
  );

  const updateProfile = useCallback(
    async (
      profileId: string,
      updates: Partial<Pick<ProtectedProfile, "name" | "mode" | "relationship" | "isActive">>,
    ) => {
      if (!user) return;
      const existing = profiles.find((p) => p.profileId === profileId);
      if (!existing) return;
      const updated = {
        ...existing,
        ...updates,
        updatedAt: Date.now(),
        privacyLevel: updates.mode ? DEFAULT_PRIVACY_BY_MODE[updates.mode] : existing.privacyLevel,
      };
      try {
        await set(ref(db, `safecircle/${user.uid}/profiles/${profileId}`), updated);
      } catch (err) {
        captureException(err as Error, { where: "useSafeCircleProfiles.updateProfile" });
      }
    },
    [user, profiles],
  );

  const removeProfile = useCallback(
    async (profileId: string) => {
      if (!user) return;
      try {
        await remove(ref(db, `safecircle/${user.uid}/profiles/${profileId}`));
      } catch (err) {
        captureException(err as Error, { where: "useSafeCircleProfiles.removeProfile" });
        return;
      }
      const activeId = useSafeCircleStore.getState().activeProfileId;
      if (activeId === profileId) {
        const remaining = profiles.filter((p) => p.profileId !== profileId);
        setActiveProfile(remaining[0]?.profileId ?? null);
      }
    },
    [user, profiles, setActiveProfile],
  );

  return { profiles, loading, addProfile, updateProfile, removeProfile };
}
